<template>
  <modal-template
    ref="modal"
    size="lg"
    :title="kindConfig.title"
    :type-modal="kindConfig.modalType"
  >
    <div class="modal-content__main">
      <d-form @submit.prevent>
        <p class="send-intro">{{ kindConfig.intro }}</p>

        <div class="send-channels">
          <d-checkbox
            :checked="channels.email"
            :disabled="sending"
            @change="toggleChannel('email')"
          >
            E-mail
          </d-checkbox>
          <d-checkbox
            :checked="channels.whatsapp"
            :disabled="sending"
            @change="toggleChannel('whatsapp')"
          >
            WhatsApp
          </d-checkbox>
        </div>

        <div v-if="!canSend" class="send-empty">
          <p>Selecione ao menos um canal para editar a mensagem.</p>
        </div>
        <d-tabs v-else ref="tabs" class="send-tabs">
          <d-tab v-if="channels.email" title="<i class='fas fa-envelope mr-1'></i> E-mail" active>
          <d-form-row>
            <d-col md="12" class="form-group">
              <label>Destinatários</label>
              <d-form-input
                type="text"
                :id="kindConfig.emailInputId"
                placeholder="email@dominio.com,email2@dominio.com"
                v-model="model.email"
                :disabled="sending"
              />
              <small class="send-hint">
                Para múltiplos emails use a vírgula como separador ex: email@dominio.com,email2@dominio.com
              </small>
            </d-col>
          </d-form-row>

          <d-form-row>
            <d-col md="12" class="form-group mb-2">
              <label>Mensagem</label>
              <editor ref="recibo" :model.sync="model.sendMessage" :class="{ 'send-editor-locked': sending }"/>
            </d-col>
          </d-form-row>
          <p class="send-note">{{ kindConfig.emailNote }}</p>
          </d-tab>
          <d-tab v-if="channels.whatsapp" title="<i class='fab fa-whatsapp mr-1'></i> WhatsApp">
          <d-form-row>
            <d-col md="12" class="form-group">
              <label>Telefone</label>
              <d-form-input
                type="text"
                :id="kindConfig.phoneInputId"
                placeholder="11999999999"
                v-model="model.phone"
                :disabled="sending"
              />
            </d-col>
          </d-form-row>

          <d-form-row>
            <d-col md="12" class="form-group mb-2">
              <label>Mensagem</label>
              <textarea class="form-control" rows="10" v-model="model.sendMessageWhats" :disabled="sending"></textarea>
            </d-col>
          </d-form-row>
          <p class="send-note">{{ kindConfig.whatsappNote }}</p>
          </d-tab>
        </d-tabs>
      </d-form>
    </div>

    <div slot="actions" class="modal-content__control" >
      <d-button
        type="button"
        :disabled="!canSend || sending"
        @click="send()"
        class="btn btn-sm btn-primary"
      >
        {{ sending ? 'Enviando...' : 'Enviar' }}
      </d-button>
    </div>
  </modal-template>
</template>

<script>
import { mapActions } from 'vuex';
import Editor from '_shared/Editor.vue';
import Events from '_utils/events';
import { openUrlInNewTab } from '_utils/require';
import ModalTemplate from '_shared/Modal.vue';
import QuotationService from '_services/rest/modules/quotation';
import MixinFormatMessage from '_common/mixins/FormatMessage';
import { MODEL, _OPEN_MODAL_ } from '../QuotationModel';

const CHANNEL_ORDER = ['email', 'whatsapp'];
const EMPTY_MESSAGES = { sendMessage: '', sendMessageWhats: '' };
const CHANNELS = {
  email: {
    label: 'E-mail',
    success: 'E-mail enviado com sucesso',
    messageKey: 'sendMessage',
    recipientKey: 'email',
  },
  whatsapp: {
    label: 'WhatsApp',
    success: 'WhatsApp enviado com sucesso',
    messageKey: 'sendMessageWhats',
    recipientKey: 'phone',
  },
};
const KINDS = {
  quotation: {
    modalType: 'send_quotation_modal',
    title: 'Enviar orçamento',
    intro: 'Selecione um ou mais canais para enviar o orçamento.',
    emailNote: 'O orçamento será anexado automaticamente.',
    whatsappNote: 'O link do orçamento será enviado na mensagem.',
    success: 'Orçamento enviado com sucesso',
    path: '',
    emailInputId: 'feInputFooter',
    phoneInputId: 'feInputWhatsappPhone',
  },
  presentation: {
    modalType: 'send_presentation_modal',
    title: 'Enviar apresentação',
    intro: 'Selecione um ou mais canais para enviar a apresentação.',
    emailNote: 'A apresentação será anexada automaticamente.',
    whatsappNote: 'O link da apresentação será enviado na mensagem.',
    success: 'Apresentação enviada com sucesso',
    path: '/presentation',
    emailInputId: 'feInputPresentationEmail',
    phoneInputId: 'feInputPresentationPhone',
  },
};

export default {
  name: 'send-channels-modal',
  props: {
    kind: {
      type: String,
      required: true,
      validator: value => Object.keys(KINDS).indexOf(value) !== -1,
    },
  },
  data() {
    return {
      sending: false,
      pendingCount: 0,
      sendGeneration: 0,
      channels: {
        email: false,
        whatsapp: false,
      },
      model: {
        email: '',
        phone: '',
        sendMessage: '',
        sendMessageWhats: '',
      },
      quotation: { ...MODEL },
    };
  },
  mixins: [MixinFormatMessage],
  components: {
    Editor,
    ModalTemplate,
  },
  computed: {
    kindConfig() {
      return KINDS[this.kind];
    },
    canSend() {
      return CHANNEL_ORDER.some(id => this.channels[id]);
    },
    messages() {
      return this.$store.state.settings.messages || EMPTY_MESSAGES;
    },
  },
  watch: {
    sending(locked) {
      const quill = this.$refs.recibo && this.$refs.recibo.editor;
      if (quill && typeof quill.enable === 'function') quill.enable(!locked);
    },
  },
  methods: {
    ...mapActions([
      'setToggle',
      'activeAlert',
    ]),
    toggleChannel(channel) {
      if (this.sending) return;
      this.channels[channel] = !this.channels[channel];
      this.updateTabs();
    },
    channelIndex(tab) {
      const title = tab.title || '';
      const index = CHANNEL_ORDER.findIndex(id => title.indexOf(CHANNELS[id].label) !== -1);
      return index === -1 ? CHANNEL_ORDER.length : index;
    },
    updateTabs() {
      this.$nextTick(() => {
        const tabs = this.$refs.tabs;
        if (!tabs || typeof tabs.updateTabs !== 'function') return;

        tabs.updateTabs();
        const ordered = tabs.tabs.slice().sort((current, next) => (
          this.channelIndex(current) - this.channelIndex(next)
        ));
        tabs.tabs = ordered;
        const activeIndex = ordered.findIndex(tab => tab.localActiveState);
        if (activeIndex >= 0) tabs.currentTab = activeIndex;
      });
    },
    fillMessage(template) {
      const withCustomer = this.setCustomerMessage(template || '', this.quotation.customer);
      return this.setSellerMessage(withCustomer, this.quotation);
    },
    formatMessages() {
      const customer = this.quotation.customer;
      this.model.email = customer.email;
      this.model.phone = this.quotation.customer_phone;
      CHANNEL_ORDER.forEach(id => {
        const { messageKey } = CHANNELS[id];
        this.model[messageKey] = this.fillMessage(this.messages[messageKey]);
      });
    },
    openLink(response) {
      if (!openUrlInNewTab(response)) {
        alert('Os popup do seu navegador estão bloqueados'); // eslint-disable-line
      }
    },
    setProgress(bool) {
      this.setToggle({ type: 'progress', bool });
    },
    channelParams(id) {
      const channel = CHANNELS[id];
      return {
        quotation_id: this.quotation.id,
        message: this.model[channel.messageKey],
        [channel.recipientKey]: this.model[channel.recipientKey],
      };
    },
    requestChannel(id) {
      const params = this.channelParams(id);
      const { path } = this.kindConfig;
      if (id === 'whatsapp') return QuotationService.sendMessageWhats(params, path);
      return QuotationService.sendMessage(params, path);
    },
    dispatchChannel(id, generation) {
      return this.requestChannel(id)
        .then(response => {
          if (generation === this.sendGeneration && id === 'whatsapp') {
            this.openLink(response);
          }
          return { channel: id, ok: true, response };
        })
        .catch(error => ({ channel: id, ok: false, error }))
        .finally(() => {
          if (generation === this.sendGeneration) this.pendingCount -= 1;
          if (this.pendingCount > 0) this.setProgress(true);
        });
    },
    finishSend(results, generation) {
      if (generation !== this.sendGeneration) return;

      const failed = results.filter(result => !result.ok);
      const sent = results.filter(result => result.ok);

      if (!failed.length) {
        this.setToggle({ type: this.kindConfig.modalType, bool: false });
        this.activeAlert({ message: this.kindConfig.success, theme: 'success' });
        return;
      }

      sent.forEach(result => {
        this.channels[result.channel] = false;
      });
      this.updateTabs();

      const errors = failed.map(result => {
        const label = CHANNELS[result.channel].label;
        const detail = (result.error && (result.error.data || result.error.message))
          || 'falha no envio';
        return `${label}: ${detail}`;
      }).join('<br/>');

      if (!sent.length) {
        this.activeAlert({ message: errors, theme: 'danger' });
        return;
      }

      const label = CHANNELS[sent[0].channel].success;
      this.activeAlert({ message: `${label}.<br/>${errors}`, theme: 'warning' });
    },
    send() {
      if (this.sending) return;
      if (!this.canSend) {
        this.activeAlert({ message: 'Selecione ao menos um canal de envio', theme: 'warning' });
        return;
      }

      const selected = CHANNEL_ORDER.filter(id => this.channels[id]);
      const invalid = selected.filter(id => {
        const { recipientKey, messageKey } = CHANNELS[id];
        const recipient = (this.model[recipientKey] || '').toString().trim();
        const message = (this.model[messageKey] || '').toString().trim();
        return !recipient || !message;
      });

      if (invalid.length) {
        const labels = invalid.map(id => CHANNELS[id].label).join(' e ');
        this.activeAlert({
          message: `Preencha destinatário e mensagem de ${labels} antes de enviar`,
          theme: 'warning',
        });
        return;
      }

      this.sending = true;
      const generation = this.sendGeneration;
      const tasks = selected.map(id => this.dispatchChannel(id, generation));

      this.pendingCount = tasks.length;
      this.setProgress(true);
      Promise.all(tasks)
        .then(results => this.finishSend(results, generation))
        .finally(() => {
          if (generation !== this.sendGeneration) return;
          this.sending = false;
          this.pendingCount = 0;
          this.setProgress(false);
        });
    },
    resetSendState() {
      this.sending = false;
      this.pendingCount = 0;
      CHANNEL_ORDER.forEach(id => {
        this.channels[id] = false;
      });
    },
    onOpenModal({ quotation, type }) {
      if (type !== this.kindConfig.modalType) return;
      this.sendGeneration += 1;
      this.quotation = quotation;
      this.resetSendState();
      if (this.quotation) this.getSettingsSystem();
    },
  },
  mounted() {
    Events.$on(_OPEN_MODAL_, this.onOpenModal);
  },
  beforeDestroy() {
    Events.$off(_OPEN_MODAL_, this.onOpenModal);
  },
};
</script>

<style lang="scss" scoped>
.modal-content__control {
  display: flex;
  justify-content: flex-end;
}

.send-intro {
  margin-bottom: 1rem;
  color: #5a6169;
}

.send-channels {
  display: flex;
  align-items: center;
  margin-bottom: 1.25rem;
}

.send-channels ::v-deep .custom-control + .custom-control {
  margin-left: 1.5rem;
}

.send-channels ::v-deep .custom-control {
  margin-bottom: 0;
}

.send-tabs {
  margin-bottom: 0.5rem;
}

.send-tabs ::v-deep .send-editor-locked {
  pointer-events: none;
}

.send-tabs ::v-deep .tab-pane {
  border: 1px solid #e1e5eb;
  border-top: none;
  border-radius: 0.25rem;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  padding: 0.75rem 1rem 0.5rem;
}

.send-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 8rem;
  margin-bottom: 0.5rem;
  padding: 1.5rem 1.25rem;
  border: 1px dashed #d5dae1;
  border-radius: 0.25rem;
  background: #f8f9fb;
  text-align: center;
}

.send-empty p {
  max-width: 22rem;
  margin: 0;
  color: #818ea3;
  font-size: 0.925rem;
  line-height: 1.45;
}

.send-hint,
.send-note {
  display: block;
  margin-top: 0.35rem;
  margin-bottom: 0;
  color: #9ea4ad;
  font-size: 0.8rem;
}
</style>
